import { Camera, Fingerprint, Info, PenTool, ShieldAlert } from 'lucide-react'
import { useState } from 'react'

import type { CustomerDetail, CustomerMediaType } from '@/api/types'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDateTime } from '@/lib/datetime'

import { ATTESTATION_PREFERENCE_LABELS } from './constants'
import { MediaUploadButton, WebcamCapture } from './media-capture'
import { useCustomerMediaBlob } from './media-image'
import { useUploadCustomerMedia } from './mutations'
import { CUSTOMER_PERMISSIONS, useCustomerPermissions } from './permissions'

/**
 * The customer file's media section: the passport photograph, the signature specimen, and the
 * thumbprint specimen.
 *
 * Three things this panel refuses to blur together. **Display and capture are two different
 * permissions** — `customers.media.view` streams a specimen back, `customers.media.capture` files a
 * new one, and a role can hold either without the other. **A specimen is evidence, not a photo
 * gallery** — every capture supersedes the last rather than replacing it, and this panel only ever
 * shows the current one; the history stays server-side. **The attestation preference is what the
 * verification dialog leans on** — see `verify-customer-dialog.tsx` — so it is shown here as the
 * fact it is, not as a setting this panel edits: it is set from the customer edit form, like every
 * other field on the file.
 */

const SPECIMENS: {
  mediaType: CustomerMediaType
  title: string
  icon: typeof Camera
  instruction: string
  webcam: boolean
}[] = [
  {
    mediaType: 'PassportPhoto',
    title: 'Passport photograph',
    icon: Camera,
    instruction: 'A clear, front-facing photograph, for the visual comparison a teller makes at the counter.',
    webcam: true,
  },
  {
    mediaType: 'Signature',
    title: 'Signature specimen',
    icon: PenTool,
    instruction: 'A scan or photograph of the customer’s signature, for comparison against the passbook.',
    webcam: false,
  },
  {
    mediaType: 'Thumbprint',
    title: 'Thumbprint specimen',
    icon: Fingerprint,
    instruction: 'A scan or photograph of the customer’s inked thumbprint, for comparison against the passbook.',
    webcam: false,
  },
]

/** `1536` reads as "1.5 KB", not "1536 B" — kept to one decimal, and bytes stay bytes under 1 KB. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${String(bytes)} B`
  }

  const kb = bytes / 1024
  if (kb < 1024) {
    return `${kb.toFixed(1)} KB`
  }

  return `${(kb / 1024).toFixed(1)} MB`
}

export function MediaPanel({ customer }: { customer: CustomerDetail }) {
  const permissions = useCustomerPermissions()

  return (
    <div className="space-y-4">
      {!permissions.canViewMedia ? (
        <Alert tone="info">
          <Info aria-hidden />
          <div className="space-y-1">
            <AlertTitle>Specimens are not shown to this role</AlertTitle>
            <AlertDescription>
              Your role does not hold {CUSTOMER_PERMISSIONS.mediaView}, so the images below are not
              fetched at all. Whether anything has been captured is still shown, from the customer
              record itself.
            </AlertDescription>
          </div>
        </Alert>
      ) : null}

      {!permissions.canCaptureMedia ? (
        <Alert tone="info">
          <Info aria-hidden />
          <AlertDescription>
            Your role does not hold {CUSTOMER_PERMISSIONS.mediaCapture}, so this panel is read-only:
            no upload or camera control is offered.
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Attestation preference</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={customer.attestationPreference === 'NotCaptured' ? 'warning' : 'brand'}>
              {ATTESTATION_PREFERENCE_LABELS[customer.attestationPreference]}
            </Badge>
            <p className="text-xs text-muted">
              {customer.attestationPreference === 'NotCaptured'
                ? 'Nobody has recorded how this customer attests to a transaction. Set it from the customer edit form.'
                : 'How this customer attests to a transaction at the counter — the specimen the verification dialog leads with.'}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        {SPECIMENS.map((specimen) => (
          <SpecimenCard
            key={specimen.mediaType}
            customerId={customer.id}
            specimen={specimen}
            existing={customer.media.find((m) => m.mediaType === specimen.mediaType) ?? null}
            canView={permissions.canViewMedia}
            canCapture={permissions.canCaptureMedia}
          />
        ))}
      </div>
    </div>
  )
}

function SpecimenCard({
  customerId,
  specimen,
  existing,
  canView,
  canCapture,
}: {
  customerId: string
  specimen: (typeof SPECIMENS)[number]
  existing: CustomerDetail['media'][number] | null
  canView: boolean
  canCapture: boolean
}) {
  const Icon = specimen.icon
  const blob = useCustomerMediaBlob(customerId, specimen.mediaType, canView)
  const upload = useUploadCustomerMedia(customerId)
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState<string | null>(null)

  const submit = (file: Blob) => {
    upload.mutate(
      { mediaType: specimen.mediaType, file },
      {
        onSuccess: () => {
          setPendingPreviewUrl(null)
          blob.refetch()
        },
        onError: () => {
          setPendingPreviewUrl(null)
        },
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex min-w-0 items-center gap-2">
          <Icon className="size-4 shrink-0 text-muted" aria-hidden />
          <CardTitle className="truncate">{specimen.title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex aspect-square items-center justify-center overflow-hidden rounded-control border border-border-subtle bg-surface-muted">
          {!canView ? (
            <div className="flex flex-col items-center gap-1.5 p-3 text-center">
              <ShieldAlert className="size-5 text-muted" aria-hidden />
              <p className="text-2xs text-muted">
                {existing ? 'Captured, but not shown to this role.' : 'Nothing captured.'}
              </p>
            </div>
          ) : pendingPreviewUrl ? (
            <img src={pendingPreviewUrl} alt="" className="h-full w-full object-cover opacity-60" />
          ) : blob.isPending ? (
            <Skeleton className="h-full w-full" />
          ) : blob.notCaptured ? (
            <div className="flex flex-col items-center gap-1.5 p-3 text-center">
              <Icon className="size-6 text-muted" aria-hidden />
              <p className="text-2xs text-muted">Not captured yet.</p>
            </div>
          ) : blob.error ? (
            <p className="p-3 text-center text-2xs text-danger-text">Could not be loaded.</p>
          ) : blob.url ? (
            <img src={blob.url} alt={specimen.title} className="h-full w-full object-cover" />
          ) : null}
        </div>

        {existing ? (
          <p className="text-2xs text-muted">
            Captured {formatDateTime(existing.capturedAtUtc)} · {formatBytes(existing.sizeBytes)}
          </p>
        ) : null}

        <p className="text-2xs text-muted">{specimen.instruction}</p>

        {canCapture ? (
          <div className="space-y-2">
            <MediaUploadButton
              label={existing ? 'Replace with a file' : 'Upload a file'}
              onSelect={(file) => {
                setPendingPreviewUrl(URL.createObjectURL(file))
                submit(file)
              }}
              isPending={upload.isPending}
            />
            {specimen.webcam ? (
              <WebcamCapture
                onCapture={(captured) => {
                  setPendingPreviewUrl(URL.createObjectURL(captured))
                  submit(captured)
                }}
                isPending={upload.isPending}
              />
            ) : null}
            {upload.isError ? (
              <p className="text-2xs text-danger-text">
                {upload.error.detail ?? upload.error.title}
              </p>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
